import { useCallback, useState } from 'react'
import lodashIncludes from 'lodash/includes'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { updatePlanSelection } from 'src/useCase/userTreatment'
import { getInfo } from 'src/useCase/user'
import { PATH as PATH_CHAT } from 'src/pages/chat/Chat'
import { PATH as PATH_AUTOMATIC_PLAN } from 'src/pages/automaticPlan'
import { PATH as PATH_VIDEO } from 'src/pages/video'
import { useAppSelector } from 'src/store'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import { trackStep, JOURNEY_STEP } from 'src/utils/tracker'

interface PlanSelectionOptions {
  planId?: string | number | null
  waitDoctorProposal?: boolean
}

export default function usePlanSelection(
  userTreatmentId?: string | number | null,
) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const userInfo = useAppSelector((state) => state.ui.app.authInfo)

  const handlePlanSelection = useCallback(
    async ({
      planId = null,
      waitDoctorProposal = false,
    }: PlanSelectionOptions) => {
      if (!userTreatmentId) return

      const treatment = userInfo?.user_treatments?.find(
        (t) => t.id === Number(userTreatmentId),
      )

      if (
        treatment &&
        !lodashIncludes(
          [
            USER_TREATMENT_STATE.AWAITING_PLAN,
            USER_TREATMENT_STATE.REGISTERED_INTERVIEW,
          ],
          treatment.state,
        )
      ) {
        return
      }
      try {
        setLoading(true)
        const payload: any = {
          id: userTreatmentId,
          has_selected_plan: !waitDoctorProposal,
        }

        if (planId !== null && planId !== undefined) {
          payload.plan_id = planId
        }

        // [SCOPE] .unwrap() added so a failed update rejects instead of
        // falling through — affects PlanSelection flow only.
        // Pre-existing behavior: dispatch result was ignored, so trackStep
        // fired even when updatePlanSelection failed, overcounting journey analytics.
        // New behavior: trackStep only fires after a confirmed successful update.
        await dispatch(updatePlanSelection(payload) as any).unwrap()

        trackStep(JOURNEY_STEP.PLAN_SELECTED, {
          userTreatmentId: userTreatmentId ?? undefined,
          metadata: { plan_id: planId },
        })

        let userData: any
        try {
          userData = await dispatch(
            getInfo({
              with: 'userTreatments',
            }) as any,
          ).unwrap()
        } catch {
          return
        }

        const treatmentAfter = userData?.user_treatments?.find(
          (t: { id: number }) => t.id === Number(userTreatmentId),
        )
        const stateAfter = treatmentAfter?.state
        const menuIdAfter = treatmentAfter?.menu_id

        if (stateAfter === USER_TREATMENT_STATE.SUBSCRIPTION_SUGGESTIONS) {
          navigate(PATH_AUTOMATIC_PLAN.SUGGEST_SUBSCRIPTION)
          return
        }
        if (stateAfter === USER_TREATMENT_STATE.VIDEO_INTERVIEW_SCHEDULED) {
          navigate(`${PATH_VIDEO.VIDEO_SCHEDULE}?menu_id=${menuIdAfter ?? ''}`)
          return
        }

        if (waitDoctorProposal) {
          navigate(PATH_CHAT)
        }
      } catch {
        // [SCOPE] Catch added alongside the .unwrap() change above — affects
        // PlanSelection flow only. updatePlanSelection's rejection is already
        // surfaced via the global Axios interceptor (app/updateError); this
        // catch only prevents an unhandled promise rejection at call sites
        // (e.g. ChooseServicePlan's onClick={() => handlePlanSelection(...)}).
      } finally {
        setLoading(false)
      }
    },
    [dispatch, userTreatmentId, navigate, userInfo],
  )

  return { handlePlanSelection, loading }
}
